import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { LogOut, Headset, Receipt } from "lucide-react";

const SUPPORT_LINK = { customer: "/support", workshop: "/support", tow_provider: "/support", admin: "/support-desk" };

export default function Shell({ title, subtitle, icon: Icon, accent = "primary", children, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const ring = accent === "accent" ? "from-accent to-amber-600" : "from-primary to-slate-900";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="شعار إسناد كار" width="40" height="40" loading="lazy" className="w-10 h-10 rounded-xl object-cover shadow-lg" data-testid="app-logo" />
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-lg">اسناد كار</div>
              <div className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role]}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "customer" && (
              <Button variant="ghost" className="rounded-full gap-1.5 px-3" onClick={() => navigate("/invoices")} data-testid="invoices-nav-btn">
                <Receipt className="w-5 h-5" /><span className="hidden md:inline text-sm">فواتيري</span>
              </Button>
            )}
            {SUPPORT_LINK[user?.role] && (
              <Button variant="ghost" className="rounded-full gap-1.5 px-3" onClick={() => navigate(SUPPORT_LINK[user.role])} data-testid="support-nav-btn">
                <Headset className="w-5 h-5" /><span className="hidden md:inline text-sm">{user.role === "admin" ? "خدمة العملاء" : "الدعم"}</span>
              </Button>
            )}
            <NotificationBell />
            <div className="hidden sm:flex flex-col items-end me-1">
              <span className="text-sm font-semibold">{user?.name}</span>
              <span className="text-xs text-muted-foreground">{user?.city}</span>
            </div>
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 animate-fade-up">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold">{title}</h1>
            {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          {actions}
        </div>
        {children}
      </main>
    </div>
  );
}
